import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Era5MonthWeatherReportService } from '@6grain/masofa-monolith-api-client/weather-report';
import { FilterOperator } from '@app/utils/filter-operator';
import { SortType } from '@6grain/masofa-monolith-api-client/common/model/sortType';
import { ChartDateManager } from './chart-date.manager';
import { ChartIndicatorManager } from './chart-indicator.manager';

/**
 * Point data for chart
 */
export interface PointData {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

/**
 * Result of point data loading
 */
export interface PointDataResult {
  point: PointData;
  data: number[];
  minYear: number;
  maxYear: number;
  success: boolean;
  error?: string;
}

/**
 * Manager for loading weather data for multiple monitoring points
 */
export class ChartDataLoadManager {
  constructor(
    private era5MonthService: Era5MonthWeatherReportService,
    private dateManager: ChartDateManager,
    private indicatorManager: ChartIndicatorManager
  ) {
  }

  /**
   * Load weather data for a single point
   */
  loadPointData(
    point: PointData,
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[],
    dataSource: string
  ): Observable<PointDataResult> {
    if (dataSource !== 'era5') {
      return of(this.createEmptyResult(point, labels, `Data source '${dataSource}' not supported yet`));
    }

    if (point.lat == null || point.lng == null) {
      return of(this.createEmptyResult(point, labels, 'No coordinates'));
    }

    return this.loadEra5PointData(point, startDate, endDate, indicator, labels);
  }

  /**
   * Load weather data for multiple points in parallel
   */
  loadMultiplePointsData(
    points: PointData[],
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[],
    dataSource: string
  ): Observable<PointDataResult[]> {
    if (!points || points.length === 0) {
      return of([]);
    }

    // Only ERA5 data source is supported for now
    if (dataSource !== 'era5') {
      return of(points.map(point => this.createEmptyResult(point, labels, `Data source '${dataSource}' not supported yet`)));
    }

    const requests = points.map(point =>
      this.loadPointData(point, startDate, endDate, indicator, labels, dataSource)
    );

    return forkJoin(requests);
  }

  /**
   * Load ERA5 monthly data for a monitoring point by coordinates (nearest grid cell).
   */
  private loadEra5PointData(
    point: PointData,
    startDate: Date | null,
    endDate: Date | null,
    indicator: string,
    labels: string[]
  ): Observable<PointDataResult> {
    const dateParams = this.dateManager.getDateRangeParams(startDate, endDate);
    const years: number[] = [];
    for (let year = dateParams.startYear; year <= dateParams.endYear; year++) {
      years.push(year);
    }

    if (years.length === 0) {
      return of(this.createEmptyResult(point, labels, 'Invalid date range'));
    }

    const yearRequests = years.map(year =>
      this.era5MonthService.weatherReportsEra5MonthWeatherReportGetByCoordinatesAndDateInRadiusPost(
        point.lat,
        point.lng,
        {
          filters: [
            {
              filterField: 'Year',
              filterOperator: FilterOperator.Equals,
              filterValue: year.toString()
            }
          ],
          offset: 0,
          sortBy: 'Year',
          sort: SortType.NUMBER_0,
          take: 12
        }
      ).pipe(
        catchError(error => {
          console.error(`Error loading station data for ${point.name}:`, error);
          return of([]);
        })
      )
    );

    return forkJoin(yearRequests).pipe(
      map(yearResponses => this.processResponse(yearResponses.flat(), point, indicator, labels)),
      catchError(error => {
        console.error(`Error loading data for point ${point.name}:`, error);
        return of(this.createEmptyResult(point, labels, error?.message));
      })
    );
  }

  /**
   * Process API response into chart data
   */
  private processResponse(
    response: any[] | null,
    point: PointData,
    indicator: string,
    labels: string[]
  ): PointDataResult {
    if (!response || !Array.isArray(response) || response.length === 0) {
      return this.createEmptyResult(point, labels, 'No data available');
    }

    // Build data map keyed by YYYY-MM
    const dataByKey = new Map<string, any>();
    response.forEach(item => {
      const itemYear = item.year || item.Year;
      const itemMonth = item.month || item.Month;
      if (itemYear != null && itemMonth != null) {
        const key = `${itemYear}-${String(itemMonth).padStart(2, '0')}`;
        dataByKey.set(key, item);
      }
    });

    // Extract values for each label
    const values: number[] = labels.map(label => {
      const monthData = dataByKey.get(label);
      if (monthData) {
        return this.indicatorManager.extractIndicatorValue(monthData, indicator);
      }
      return 0;
    });

    // Calculate year range
    const years = response.map(item => item.year || item.Year).filter(y => y != null);
    const minYear = years.length > 0 ? Math.min(...years) : 0;
    const maxYear = years.length > 0 ? Math.max(...years) : 0;

    return {
      point,
      data: values,
      minYear,
      maxYear,
      success: true
    };
  }

  /**
   * Create an empty result for error cases
   */
  private createEmptyResult(point: PointData, labels: string[], error?: string): PointDataResult {
    return {
      point,
      data: Array(labels.length).fill(0),
      minYear: 0,
      maxYear: 0,
      success: false,
      error
    };
  }
}
